import * as z from 'zod/v4';
import {
  colorSchema,
  eventTaskSchema,
  localizedStringSchema,
  rewardMessageSchema,
  rewardSchema,
  tsvConfigSchema,
} from './event-shared-object.schema';
import { imageSchema } from './event-shared-object.schema';
import { userTargetSchema } from './event-user-targeting.schema';
import { ImageEnums, RewardItemEnums, UserTargetEnums, HUDIconEnums, TileEnums } from './types';
import { EnumParams } from '../events.types';
import { collectionSchema } from '../../collections';

export const schedulePermanentSchema = z.object({
  permanent: z.literal(true),
  offsetTime: z.iso.duration().optional(),
  endOffsetTime: z.iso.duration().optional(),
  duration: z.iso.duration().optional(),
});

export const scheduleNonPermanentSchema = z.object({
  permanent: z.literal(false),
  start: z.coerce.date(),
  end: z.coerce.date().optional(),
  visibleAfter: z.coerce.date().optional(),
  duration: z.iso.duration().optional(),
});

export const scheduleSectionSchema = z.discriminatedUnion('permanent', [
  scheduleNonPermanentSchema,
  schedulePermanentSchema,
]);

const getDisplaySchema = <T extends Record<string, z.ZodTypeAny>>(schema: T) => {
  return z.discriminatedUnion('displayed', [
    z.object({
      ...schema,
      displayed: z.literal(true),
    }),
    z.object({
      displayed: z.literal(false),
    }),
  ]);
};

export const badgeSchema = (enums?: ImageEnums) =>
  z.object({
    text1: localizedStringSchema.optional(),
    text2: localizedStringSchema.optional(),
    image: imageSchema(enums).optional(),
    color: colorSchema.optional(),
  });

const displaySchemaContent = (enums?: ImageEnums) => ({
  backgroundImage: imageSchema(enums).optional(),
  title: localizedStringSchema,
  summary: localizedStringSchema.optional(),
  details: localizedStringSchema.optional(),
  buttonText: localizedStringSchema.optional(),
  buttonAction: z.url().optional(),
  badge: badgeSchema(enums).optional(),
});

export const displaySchema = (enums?: ImageEnums) => getDisplaySchema(displaySchemaContent(enums));

export const hudIconSchema = (enums?: ImageEnums & HUDIconEnums) =>
  getDisplaySchema({
    image: imageSchema(enums),
    title: localizedStringSchema.optional(),
    jiggle: enums ? z.enum(enums.JiggleType).optional() : z.string().optional(),
  });

export const interstitialSchema = () =>
  z
    .object({
      behavior: z
        .enum(['Always', 'Never', 'WithHUDIcon', 'Targeted'])
        .optional()
        .default('WithHUDIcon'),
      order: z.number().nonnegative().int().optional(),
      targeting: z
        .object({
          snapshotCriteria: z.boolean().default(false),
          targets: z.any().optional(),
        })
        .optional(),
    })
    .optional();

export const fullScreenSchema = (enums?: ImageEnums) =>
  getDisplaySchema({
    ...displaySchemaContent(enums),
    bannerColor: colorSchema.optional(),
    containerColor: colorSchema.optional(),
    cardBackgroundColor: colorSchema.optional(),
    cardTitleColor: colorSchema.optional(),
    interstitial: interstitialSchema(),
  });

export const tileSchemaFields = (enums?: ImageEnums & TileEnums) => ({
  ...displaySchemaContent(enums),
  cardTitleColor: colorSchema.optional(),
  cardColor: colorSchema.optional(),
  cardTitleImage: imageSchema(enums).optional(),
  cardFrame: imageSchema(enums).optional(),
  cardFrameColor: colorSchema.optional(),
  hideFromMainShop: z.boolean().optional().default(false),
  fullWidthDisplay: z.boolean().optional().default(false),
  contentImage: imageSchema(enums).optional(),
  backgroundGradientImage: imageSchema(enums).optional(),
  rewardsAlignment: (enums ? z.enum(enums.RewardsAlignment) : z.string()).optional(),
  offerCollection: collectionSchema
    .pick({
      id: true,
      title: true,
      bannerColor: true,
      backgroundColor: true,
      displayTitleInGame: true,
    })
    .optional(),
});

export const tileSchema = (enums?: ImageEnums & TileEnums) =>
  getDisplaySchema(tileSchemaFields(enums));

export const cardDisplaySectionSchema = (enums?: ImageEnums & HUDIconEnums & TileEnums) =>
  z.object({
    hudIcon: hudIconSchema(enums),
    tile: tileSchema(enums),
    fullScreen: fullScreenSchema(enums),
    sortOrder: z.number().nonnegative().int(),
    featureFirstReward: z.boolean().optional(),
  });

export const userTargetingSectionSchema = (enums?: UserTargetEnums) =>
  z.object({
    snapshotCriteria: z.boolean().default(false),
    targets: userTargetSchema(enums).optional(),
  });

const sendTimeRelationshipSchema = z.enum(['before_end', 'after_start']);

export const pushNotificationSchema = z.object({
  title: localizedStringSchema.optional(),
  message: localizedStringSchema.optional(),
  sendTimeRelationship: sendTimeRelationshipSchema,
  offsetTime: z.string().duration(),
  type: z.string(),
});

export const notificationSectionSchema = z.object({
  pushNotifications: z.array(pushNotificationSchema).optional(),
});

export const rewardSectionSchema = (enums?: RewardItemEnums) =>
  z.object({
    rewards: z.array(rewardSchema(enums)),
    message: rewardMessageSchema(enums).optional(),
  });

export const purchaseSectionSchema = (enums?: EnumParams) =>
  z.object({
    price: priceSchema(enums),
    maxRewardTimes: z.number().nonnegative().int(),
  });

export const priceSchema = (enums?: EnumParams) =>
  z.discriminatedUnion('category', [
    z.object({
      category: z.literal('iap'),
      type: enums ? z.enum(enums.IAPProduct) : z.string(),
      amount: z.undefined(),
    }),
    z.object({
      category: z.literal('resource'),
      type: enums ? z.enum(enums.ResourceType) : z.string(),
      amount: z.number().nonnegative().int().optional(),
    }),
    z.object({
      category: z.literal('rv'),
      type: z.undefined(),
      amount: z.undefined(),
    }),
  ]);

export const chainOfferTileSchema = (enums?: EnumParams) =>
  z.object({
    price: priceSchema(enums),
    rewards: z.array(rewardSchema(enums)).min(1),
  });

export const chainOfferSectionSchema = (enums?: EnumParams) =>
  z.object({
    altCardTitleColor: colorSchema.optional(),
    rewardMessage: rewardMessageSchema(enums).optional(),
    shouldLoop: z.boolean().optional(),
    tiles: z.array(chainOfferTileSchema(enums)).min(1),
  });

export const stepUpOfferOfferSchema = (enums?: EnumParams) =>
  z.object({
    display: displaySchema(enums),
    tabDisplay: z.object({
      displayPrice: z.boolean(),
      text: localizedStringSchema.optional(),
    }),
    price: priceSchema(enums),
    days: z
      .array(
        z.object({
          day: z.number().int().min(1).max(3),
          rewards: z.array(rewardSchema(enums)).min(1),
        }),
      )
      .length(3),
  });

export const stepUpOfferSectionSchema = (enums?: EnumParams) =>
  z.object({
    offers: z.array(stepUpOfferOfferSchema(enums)).max(3),
    hideTabsUntilFirstPurchase: z.boolean(),
    message: rewardMessageSchema(enums).optional(),
  });

export const growthFundTrackSchema = (enums?: EnumParams) =>
  z.object({
    showValueTab: z.boolean().optional().default(false),
    title: localizedStringSchema,
    price: priceSchema(enums),
    badge: badgeSchema(enums).optional(),
  });

export const growthFundTierSchema = (enums?: EnumParams) =>
  z.object({
    pointsRequired: z.number().nonnegative().int(),
    freeRewards: z.array(rewardSchema(enums)),
    paidRewards: z.array(z.array(rewardSchema(enums))),
  });

export const growthFundSectionSchema = (enums?: EnumParams) =>
  z.object({
    tracks: z.array(growthFundTrackSchema(enums)),
    tiers: z.array(growthFundTierSchema(enums)),
    message: rewardMessageSchema(enums).optional(),
    task: eventTaskSchema(enums),
    showProgressBar: z.boolean().default(false),
    showOnPvpScreen: z.boolean().optional().default(false),
  });

export const piggyBankSectionSchema = (enums?: EnumParams) =>
  z.object({
    startingAmount: z.number().nonnegative().int(),
    maxAmount: z.number().nonnegative().int(),
    price: priceSchema(enums),
    message: rewardMessageSchema(enums).optional(),
    task: eventTaskSchema(enums),
    rewardPerTask: rewardSchema(enums),
    size: enums?.PiggyBankSize ? z.enum(enums.PiggyBankSize) : z.string(),
    fullDuration: z.string().duration(),
  });

export const wallOfferSectionSchema = (enums?: EnumParams) =>
  z.object({
    heroes: z
      .array(
        z.object({
          hero: enums ? z.enum(enums.HeroType) : z.string(),
          price: priceSchema(enums),
        }),
      )
      .max(3)
      .min(1),
  });

export const exchangeShopSectionSchema = (enums?: EnumParams) =>
  z.object({
    showOnPvpScreen: z.boolean().optional().default(false),
    maxInventoryItemId: z.number().int().nonnegative().optional(),
    inventory: z.array(
      z.object({
        id: z.number().int().positive().optional(),
        item: rewardSchema(enums),
        price: priceSchema(enums),
        limit: z.number().nonnegative().int(),
      }),
    ),
  });

export const eventQuestSectionSchema = (enums?: EnumParams) =>
  z.object({
    backfill: z.boolean().optional(),
    showOnPvpScreen: z.boolean().optional().default(false),
    maxQuestId: z.number().int().nonnegative().optional(),
    quests: z.array(
      z.object({
        id: z.number().int().positive().optional(),
        task: eventTaskSchema(enums),
        target: z.number().nonnegative().int(),
        rewards: z.array(rewardSchema(enums)),
        buttonAction: z.string().url().optional(),
      }),
    ),
  });

export const eventCurrencySectionSchema = (enums?: EnumParams) =>
  z.object({
    type: enums ? z.enum(enums.EventCurrencyType) : z.string(),
    name: localizedStringSchema.optional(),
    description: localizedStringSchema.optional(),
    image: imageSchema(enums).optional(),
    rarity: (enums ? z.enum(enums.Rarity) : z.string()).optional(),
    category: (enums ? z.enum(enums.ResourceCategory) : z.string()).optional(),
    capacity: z.number().int().nonnegative().optional(),
    convertsTo: (enums ? z.enum(enums.ResourceType) : z.string()).optional(),
    convertsRation: z.number().nonnegative().optional(),
  });

export const eventOverrideSectionSchema = (enums?: EnumParams) =>
  z.object({
    statType: enums ? z.enum(enums.GameStatType) : z.string(),
    data: z.string(),
  });

export const eventBoxSectionSchema = (enums?: EnumParams) =>
  z
    .object({
      type: enums ? z.enum(enums.EventBoxType) : z.string(),
      name: localizedStringSchema.optional(),
      description: localizedStringSchema.optional(),
      image: imageSchema(enums).optional(),
      rarity: (enums ? z.enum(enums.Rarity) : z.string()).optional(),
    })
    .and(tsvConfigSchema(enums, false));

export { colorSchema };
